const GEO_WEATHER_API_URL = "https://geow.adctv.com";

export function mergeContextObjects(baseValue, incomingValue) {
    const baseIsObject = baseValue && typeof baseValue === "object" && !Array.isArray(baseValue);
    const incomingIsObject = incomingValue && typeof incomingValue === "object" && !Array.isArray(incomingValue);

    if (!incomingIsObject) {
        return incomingValue;
    }

    const merged = baseIsObject ? { ...baseValue } : {};
    Object.keys(incomingValue).forEach((key) => {
        merged[key] = mergeContextObjects(merged[key], incomingValue[key]);
    });

    return merged;
}

function getDeviceOs(userAgent) {
    if (/iphone|ipad|ipod/i.test(userAgent)) return "ios";
    if (/android/i.test(userAgent)) return "android";
    if (/windows/i.test(userAgent)) return "windows";
    if (/mac os x|macintosh/i.test(userAgent)) return "macos";
    if (/linux/i.test(userAgent)) return "linux";
    return "unknown";
}

function getDeviceBrowser(userAgent) {
    if (/edg\//i.test(userAgent)) return "edge";
    if (/opr\//i.test(userAgent) || /opera/i.test(userAgent)) return "opera";
    if (/firefox/i.test(userAgent)) return "firefox";
    if (/chrome|crios/i.test(userAgent) && !/edg\//i.test(userAgent) && !/opr\//i.test(userAgent)) return "chrome";
    if (/safari/i.test(userAgent) && !/chrome|crios|edg\//i.test(userAgent)) return "safari";
    return "unknown";
}

function getScreenSizeTier(width) {
    if (width < 480) return "small";
    if (width <= 768) return "medium";
    if (width <= 1440) return "large";
    return "xl";
}

function getViewportSizeTier(width) {
    if (width < 480) return "xs";
    if (width <= 640) return "sm";
    if (width <= 768) return "md";
    if (width <= 1024) return "lg";
    return "xl";
}

export function buildTimeContext(now = new Date()) {
    const pad = (value) => String(value).padStart(2, "0");
    let timezone = null;
    try {
        timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch (error) {
        console.error("Unable to resolve the local time zone:", error);
    }

    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const day = now.getDate();
    const hour = now.getHours();
    const minute = now.getMinutes();
    const second = now.getSeconds();
    const dayOfWeek = now.getDay();
    const date = `${year}-${pad(month)}-${pad(day)}`;
    const time = `${pad(hour)}:${pad(minute)}:${pad(second)}`;

    const utcYear = now.getUTCFullYear();
    const utcMonth = now.getUTCMonth() + 1;
    const utcDay = now.getUTCDate();
    const utcHour = now.getUTCHours();
    const utcDayOfWeek = now.getUTCDay();
    const utcDate = `${utcYear}-${pad(utcMonth)}-${pad(utcDay)}`;
    const utcTime = `${pad(utcHour)}:${pad(now.getUTCMinutes())}:${pad(now.getUTCSeconds())}`;
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const isWorkHours = hour >= 9 && hour < 18 && !isWeekend;

    return {
        time: {
            date,
            time,
            datetime: `${date}T${time}`,
            year,
            month,
            day,
            day_of_week: dayOfWeek,
            hour,
            minute,
            timezone,
            daypart: hour >= 5 && hour < 8 ? "early_morning"
                : hour >= 8 && hour < 12 ? "morning"
                    : hour >= 12 && hour < 17 ? "afternoon"
                        : hour >= 17 && hour < 21 ? "evening"
                            : "night",
            is_weekend: isWeekend,
            is_work_hours: isWorkHours,
            is_commuting_hours: (hour >= 7 && hour <= 10) || (hour >= 17 && hour <= 20),
            meal_period: hour >= 6 && hour < 12 ? "breakfast"
                : hour >= 12 && hour < 16 ? "lunch"
                    : hour >= 16 && hour < 19 ? "snack"
                        : hour >= 19 && hour < 23 ? "dinner"
                            : "late_night",
            productivity_state: hour < 5 ? "sleeping" : isWorkHours ? "productive" : "relaxed",
        },
        utc: {
            date: utcDate,
            time: utcTime,
            datetime: now.toISOString(),
            year: utcYear,
            month: utcMonth,
            day: utcDay,
            day_of_week: utcDayOfWeek,
            hour: utcHour,
        },
    };
}

export function buildDerivedContext() {
    const userAgent = (typeof navigator !== "undefined" ? navigator.userAgent : "").toLowerCase();
    const viewportWidth = (typeof window !== "undefined" ? window.innerWidth : 0)
        || (typeof document !== "undefined" ? document.documentElement.clientWidth : 0)
        || (typeof screen !== "undefined" ? screen.width : 0);
    const viewportHeight = (typeof window !== "undefined" ? window.innerHeight : 0)
        || (typeof document !== "undefined" ? document.documentElement.clientHeight : 0)
        || (typeof screen !== "undefined" ? screen.height : 0);
    const screenWidth = (typeof window !== "undefined" && window.screen?.width) || viewportWidth;
    const orientation = viewportWidth > viewportHeight ? "landscape" : "portrait";
    const isTv = /smart-tv|smarttv|googletv|appletv|hbbtv|netcast|viera|tizen|web0s|webos|roku|aftb|aftm|bravia|crkey|tv/i.test(userAgent);
    const isTablet = !isTv && (/ipad|tablet/i.test(userAgent) || (/android/i.test(userAgent) && viewportWidth >= 768 && viewportWidth <= 1024));
    const isMobile = !isTv && !isTablet && (/mobi|iphone|ipod|android/i.test(userAgent) || viewportWidth < 768);
    const deviceType = isTv ? "tv" : isTablet ? "tablet" : isMobile ? "mobile" : "desktop";

    return {
        ...buildTimeContext(),
        device: {
            type: deviceType,
            is_mobile: isMobile,
            is_tablet: isTablet,
            is_desktop: !isMobile && !isTablet && !isTv,
            os: getDeviceOs(userAgent),
            browser: getDeviceBrowser(userAgent),
            orientation,
            screen_size_tier: getScreenSizeTier(screenWidth),
        },
        viewport: {
            size_tier: getViewportSizeTier(viewportWidth),
            orientation,
            width: viewportWidth,
            height: viewportHeight,
        },
    };
}

export function ensureDerivedContext() {
    const existingContext = window.__ADCTVContext__ && typeof window.__ADCTVContext__ === "object"
        ? window.__ADCTVContext__
        : {};

    window.__ADCTVContext__ = mergeContextObjects(existingContext, buildDerivedContext());
}

function isPlainObject(value) {
    return value && typeof value === "object" && !Array.isArray(value);
}

/** Same usable-geo gate as tracker.js / tag.ins (lat or country present). */
export function hasUsableGeoContext(context = window.__ADCTVContext__) {
    const geo = isPlainObject(context) ? context.geo : null;
    return !!(isPlainObject(geo) && (geo.lat != null || geo.country != null));
}

function getFallbackGeoContext() {
    return {
        geo: { country: null, city: null, region: null, lat: null, lng: null, timezone: null },
        network: { isp: null, asn: null, protocol: null, rtt: null, speed_tier: null, supports_rich_media: null, video_quality_tier: null },
        bot: { score: null, is_bot: null, is_corporate_proxy: null },
        time: { timestamp: null, hour: null, day_of_week: null, timezone: null, daypart: null, is_weekend: null, is_work_hours: null, is_commuting_hours: null, meal_period: null, productivity_state: null },
        weather: { temp: null, feels_like: null, local_hour: null, humidity: null, condition: null, icon: "//wcdn.airtory.com/weather/64x64/day/116.png", text: null, is_day: null, precip: null, wind_speed: null, uv_index: null, sunrise: null, sunset: null, visibility: null, temperature_band: null, condition_type: null, uv_level: null, comfort_level: null, wind_level: null, is_daylight: null, precipitation_level: null, visibility_level: null, activity_suitability: null },
        audience: { outdoor_score: null, outdoor_likelihood: null, commerce_intent: null, engagement_likelihood: null, mobility_state: null, session_mood: null, purchase_context: null },
        traffic: { quality_tier: null, is_suspicious: null, trust_score: null },
        device: { rich_media_ready: null, experience_tier: null, type: null, is_mobile: null, is_tablet: null, is_desktop: null, os: null, browser: null, orientation: null, screen_size_tier: null },
        viewport: { size_tier: null, orientation: null },
        media: { video_quality: null },
    };
}

function dispatchContextReady() {
    if (typeof document !== "undefined") {
        document.dispatchEvent(new CustomEvent("adctv-context-ready", { bubbles: true }));
    }
}

/**
 * Fetches geo/weather context once and stores it on window.__ADCTVContext__.
 * Mirrors App.vue behavior so preview iframes can reuse the preloaded context.
 *
 * Non-blocking contract: callers MUST NOT await this before creative / Preview init.
 * Fire-and-forget is correct; the returned / __adctvGeoFetchPromise can be observed later.
 * Failures always resolve (never reject) so there is no unhandledrejection.
 */
export function preloadGeoContext() {
    ensureDerivedContext();

    // Single-flight: return the in-flight promise (do not resolve early with incomplete geo).
    if (window.__adctvGeoFetchPromise) {
        window.geoApiRendered = true;
        return window.__adctvGeoFetchPromise;
    }

    if (hasUsableGeoContext()) {
        window.geoApiRendered = true;
        return Promise.resolve(window.__ADCTVContext__);
    }

    window.geoApiRendered = true;

    window.__adctvGeoFetchPromise = fetch(GEO_WEATHER_API_URL)
        .then((response) => response.text())
        .then((scriptContent) => {
            const fetchedContext = scriptContent ? JSON.parse(scriptContent)?.context : {};
            const existingContext = window.__ADCTVContext__ && typeof window.__ADCTVContext__ === "object"
                ? window.__ADCTVContext__
                : {};
            window.__ADCTVContext__ = mergeContextObjects(
                existingContext,
                fetchedContext && typeof fetchedContext === "object" ? fetchedContext : {}
            );
            ensureDerivedContext();
            dispatchContextReady();
            return window.__ADCTVContext__;
        })
        .catch((error) => {
            console.error("Error loading Geo Weather API:", error);
            const existingContext = window.__ADCTVContext__ && typeof window.__ADCTVContext__ === "object"
                ? window.__ADCTVContext__
                : {};
            window.__ADCTVContext__ = mergeContextObjects(existingContext, getFallbackGeoContext());
            ensureDerivedContext();
            dispatchContextReady();
            return window.__ADCTVContext__;
        });

    return window.__adctvGeoFetchPromise;
}
